import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Row, Column, Button, List } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, CTA, Image } from '../../components';
import { getShowroomByID } from '../../actions/location-actions';
import { GET_PROMOS_BY_BRANCH } from '../../enums/graphql';
import { formatPhoneNumber, getSessionStorage, stripTags } from '../../components/utilities/functions';
import { useBranchShowrooms } from '../../hooks/use-branch-showrooms';
import { useReviews } from '../../hooks/use-reviews';
import { useGlobalConfig } from '../../hooks/use-global-config';
import { isValidArray } from '../../components/utilities/validations';
import CardSection from '../../components/common/card-section';
import NearbyShowrooms from '../../components/template/branch-showroom/nearby-showrooms';
import DynamicComponent from '../../components/common/dynamic-component';
import ReviewsSlider from '../../components/common/reviews-slider';
import LocalNavTop from '../../components/common/local-nav-top';
import VisualJourneyModal from '../../components/page/support/visual-journey-modal';
import MapCollage from '../../components/template/branch-showroom/map-collage';
import ElfSightEmbed from '../../components/common/elfsight-embed';
import LocalShowroom from '../../components/common/structured-data/local-showroom';
import './index.css';
import PromoBanner from '../../components/common/promo-banner';
import { useMediaQuery } from 'react-responsive';

const BranchShowroom = (props) => {
	const pageData = props.data.contentstackBranchShowroom;
	const { showroom: configData } = useGlobalConfig();
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const [showroomData, setShowroomData] = useState(props?.data?.showroom);
	const [isOpen, setIsOpen] = useState(null);
	const combinedShowrooms = [...props.data.showroomsByBranch?.nodes, ...props.data.showroomsByStoreNumber?.nodes];
	const isESRatings = pageData.panels?.find((panel) => panel.reviews != null && panel.reviews.showroom_ratings_id);
	const { showrooms, error } = useBranchShowrooms(pageData.branch_data?.[0]?.branch_retail, combinedShowrooms, pageData.branch_data?.[0]?.additional_showrooms);
	const { status, reviews } = useReviews('branch', isESRatings ? null : pageData.branch_id);
	const skipPromoCall = !pageData.branch_id || pageData.hide_promo || pageData.branch_data?.[0]?.hide_promo ? true : false;
	const { data } = useQuery(GET_PROMOS_BY_BRANCH, { variables: { branchIDs: pageData.branch_id }, skip: skipPromoCall });
	const memberOfArray = pageData.panels?.find((panel) => panel.cards != null && /(memberships|partnerships)/i.test(panel.cards.heading))?.cards?.cards || [];
	const awardArray = pageData.panels?.find((panel) => panel.cards != null && /(awards)/i.test(panel.cards.heading))?.cards?.cards || [];
	const funderArray = pageData.panels?.find((panel) => panel.cards != null && /(giving|sponsor)/i.test(panel.cards.heading))?.cards?.cards || [];
	const socialArray = pageData.panels?.find((panel) => panel.about != null)?.about?.social || [];
	const isTablet = useMediaQuery({ minWidth: 768 });

	useEffect(() => {
		dispatch(getShowroomByID(pageData.showroom_id));
	}, [pageData, dispatch]);

	useEffect(() => {
		if (locations?.showroom_info?.store_number == pageData?.showroom_id) setShowroomData(locations?.showroom_info);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only needs to check for store_number.. api call vs redux to fix?
	}, [locations?.showroom_info?.store_number, pageData?.showroom_id]);

	const onModalCloseClick = () => setIsOpen(null);

	const onScheduleBtnClick = (e) => {
		if (getSessionStorage('journeyOptOut')) return;

		e.preventDefault();
		setIsOpen('visual-journey');
	};

	const mapAddress = `${showroomData?.line_1}${showroomData?.line_2 != null ? '+' + showroomData?.line_2 : ''}+${showroomData?.city},+${showroomData?.state}+${showroomData?.postal_code}`;

	return (
		<Layout id='template-BranchShowroom'>
			{isValidArray(pageData?.branch_data?.[0]?.local_nav) && (
				<LocalNavTop
					branchData={pageData.branch_data[0]}
					linksArray={pageData?.branch_data?.[0].local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			<div className='branch-showroomInfo mt-6 pb-6 md:pb-8 xl:pb-10'>
				<Container>
					<div className='grid grid-cols-1 gap-y-4 xl:grid-cols-2 xl:grid-rows-[auto_43px_repeat(5,_auto)] xl:gap-x-8 xl:gap-y-6'>
						<div className='colHeading col-start-1 row-start-1 md:col-span-2 xl:col-span-1'>
							{pageData.page_heading && (
								<h1
									className='text-h2 [&>span]:text-overline [&>span]:block'
									dangerouslySetInnerHTML={{ __html: pageData.page_heading }}
								/>
							)}

							{pageData.also_serving && (
								<div
									className='mb-4 mt-1.5 text-sm xl:mb-3 xl:mt-0.5 xl:text-xl'
									dangerouslySetInnerHTML={{ __html: pageData.also_serving }}
								/>
							)}

							{isValidArray(configData?.cta) && (
								<List
									bullets='none'
									className='flex flex-wrap max-md:space-y-2 md:space-x-2 xl:space-x-3'
								>
									{configData.cta.map((cta, index) => {
										if (cta.link?.href?.includes('schedule-consultation') && pageData.branch_data?.[0]?.hide_rta) return;

										return (
											<li
												key={index}
												className='flex-12/12 md:flex-none md:max-xl:w-72 xl:max-w-[50%] xl:flex-1'
											>
												<CTA
													data={cta}
													onClick={index === 1 && onScheduleBtnClick}
													size='sm'
													wide
												/>
											</li>
										);
									})}
								</List>
							)}
						</div>

						<div className='colAddress col-start-1 row-start-3 xl:row-span-2 xl:row-start-2'>
							<Row>
								<Column
									xs={12}
									md={6}
								>
									<div
										className='bg-gray-5 p-2 font-semibold uppercase tracking-2px xl:text-lg [&>p]:flex [&>p]:items-center [&_span]:mr-2'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'address')?.heading }}
									/>
									<div className='px-2 py-3'>
										<p className='branchShowroom-address'>
											{showroomData?.line_1}
											{showroomData?.line_2 != null ? <span className='block'>{showroomData?.line_2}</span> : <br />}
											{showroomData?.city}, {showroomData?.state} {showroomData?.postal_code}
										</p>
									</div>
								</Column>

								<Column
									xs={12}
									md={6}
									className='mt-4 md:mt-0'
								>
									<div
										className='bg-gray-5 p-2 font-semibold uppercase tracking-2px xl:text-lg [&>p]:flex [&>p]:items-center [&_span]:mr-2'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'phone')?.heading }}
									/>

									<List
										bullets='none'
										className='space-y-1.5 px-2 py-3'
									>
										{showroomData?.phone && (
											<li>
												<span
													className='mr-1 inline-block font-semibold leading-tight'
													dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'phone')?.subheading[0] }}
												/>
												{formatPhoneNumber(showroomData.phone)}
											</li>
										)}
										{showroomData?.service_number && (
											<li>
												<span
													className='mr-1 inline-block font-semibold leading-tight'
													dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'phone')?.subheading[1] }}
												/>
												{formatPhoneNumber(showroomData.service_number)}
											</li>
										)}
									</List>
								</Column>
							</Row>
						</div>

						<div className='colHours col-start-1 row-start-4 xl:row-span-2 xl:row-start-4'>
							<div
								className='bg-gray-5 p-2 font-semibold uppercase tracking-2px xl:text-lg [&>p]:flex [&>p]:items-center [&_span]:mr-2'
								dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.heading }}
							/>

							<List
								bullets='none'
								className='space-y-1.5 px-2 py-3 md:columns-2'
							>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[0] }}
									/>
									{!showroomData?.hour_mon ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_mon}
								</li>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[1] }}
									/>
									{!showroomData?.hour_tue ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_tue}
								</li>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[2] }}
									/>
									{!showroomData?.hour_wed ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_wed}
								</li>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[3] }}
									/>
									{!showroomData?.hour_thu ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_thu}
								</li>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[4] }}
									/>
									{!showroomData?.hour_fri ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_fri}
								</li>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[5] }}
									/>
									{!showroomData?.hour_sat ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_sat}
								</li>
								<li>
									<span
										className='inline-block w-28 font-semibold'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[6] }}
									/>
									{!showroomData?.hour_sun ? configData?.sections?.find((section) => section.section_id === 'hours')?.subheading[7] : showroomData?.hour_sun}
								</li>
							</List>

							{configData?.special_hours && <p className='text-caption px-2'>{configData.special_hours}</p>}
							{['772', '706'].some((id) => pageData?.branch_data?.[0]?.branch_retail?.includes(id)) && <p className='text-caption px-2'>This showroom is closed from 1:00 - 2:00pm each day for a lunch break.</p>}
						</div>

						{isValidArray(data?.getPromotionsByBranchId) && (
							<div className='colPromo col-start-1 row-start-6 xl:col-start-2 xl:row-span-3 xl:row-start-3'>
								<div className='flex items-center justify-between bg-gray-5 p-2'>
									<div
										className='font-semibold uppercase tracking-2px xl:text-lg [&>p]:flex [&>p]:items-center [&_span]:mr-2'
										dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'promotion')?.heading }}
									/>
									{isTablet && (
										<Button
											color='tertiary-alt'
											href='/where-to-buy/schedule-consultation/'
											className='text-gray-90 decoration-gray-90'
										>
											Claim Offer
										</Button>
									)}
								</div>
								<PromoBanner
									type='component'
									placement='branch-showroom'
									isCollapsible={false}
									hasHeader={false}
								/>
								{!isTablet && (
									<Button
										color='tertiary-alt'
										href='/where-to-buy/schedule-consultation/'
										className='!w-full'
									>
										Claim Offer
									</Button>
								)}
							</div>
						)}

						<div className='colMap col-start-1 row-start-2 xl:col-start-2 xl:row-span-2 xl:row-start-1'>
							<MapCollage
								mapLocation={mapAddress?.replace(/\s+/g, '+')}
								type={pageData.showroom_images?.layout}
								images={pageData.showroom_images?.image}
							/>
						</div>

						<div className='colDirections col-start-1 row-start-5 xl:row-span-2 xl:row-start-6'>
							<div
								className='bg-gray-5 p-2 font-semibold uppercase tracking-2px xl:text-lg [&>p]:flex [&>p]:items-center [&_span]:mr-2'
								dangerouslySetInnerHTML={{ __html: configData?.sections?.find((section) => section.section_id === 'directions')?.heading }}
							/>

							<div className='px-2 py-3'>
								{pageData.directions && <div dangerouslySetInnerHTML={{ __html: pageData.directions }} />}

								{configData?.sections?.find((section) => section.section_id === 'directions')?.subheading && (
									<Button
										href={`https://www.google.com/maps/place/${mapAddress?.replace(/\s+/g, '+')}`}
										color='tertiary'
										target='_blank'
										rel='noopener noreferrer'
										title='Link opens google maps in a new window'
										className='mt-4 !text-left decoration-black'
									>
										{configData.sections.find((section) => section.section_id === 'directions').subheading}
									</Button>
								)}
							</div>
						</div>
					</div>
				</Container>
			</div>

			{isValidArray(pageData.panels) &&
				pageData.panels.map((panel, index) => {
					if (isValidArray(panel.cards?.cards)) {
						return (
							<CardSection
								data={panel.cards}
								key={index}
								className={`branch-cardSection`}
							/>
						);
					}

					if (panel.about != null) {
						return (
							<div
								id={panel.about.anchor}
								className='branchShowroom-about py-6 md:py-8 xl:py-12'
								key={index}
							>
								<Container>
									{panel.about.heading && (
										<h2
											className='mb-6 font-semibold'
											dangerouslySetInnerHTML={{ __html: panel.about.heading }}
										/>
									)}
									<Row>
										{isValidArray(panel.about.image) && (
											<Column
												xs={12}
												md='auto'
											>
												<Image
													data={panel.about.image[0]}
													className='!block aspect-[3/2] w-full max-md:mb-6 md:w-[254px] xl:w-[373px]'
												/>
											</Column>
										)}
										<Column
											xs={12}
											md='fluid'
										>
											{panel.about.description && <div dangerouslySetInnerHTML={{ __html: panel.about.description }} />}
											{configData?.sections?.find((section) => section.section_id === 'directions')?.subheading && (
												<Button
													href={`https://www.google.com/maps/place/${mapAddress?.replace(/\s+/g, '+')}`}
													color='tertiary'
													target='_blank'
													rel='noopener noreferrer'
													title='Link opens google maps in a new window'
													className='mt-3 decoration-black md:mt-2.5'
												>
													{configData.sections.find((section) => section.section_id === 'directions').subheading}
												</Button>
											)}
											{isValidArray(panel.about.social) && (
												<List
													bullets='none'
													layout='inline'
													className='mt-6 space-x-3 md:mt-7 xl:mt-8'
												>
													{panel.about.social.map((social, index) => (
														<li key={index}>
															<CTA
																data={social}
																className='[&>img]:h-6'
															/>
														</li>
													))}
												</List>
											)}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (isValidArray(panel.dynamic?.dynamic)) {
						return (
							<DynamicComponent
								data={panel.dynamic}
								key={index}
								className={`${panel.dynamic.dynamic[0].card_type !== 'faq' ? 'branch-dynamic' : ''} py-6 md:py-8 xl:py-10 ${panel.dynamic.gray_background ? 'bg-gray-5' : ''}`}
								hideStructuredData={panel.dynamic.dynamic[0].card_type === 'faq'}
							/>
						);
					}

					if (panel.reviews != null && !panel.reviews.showroom_ratings_id && status !== 'loading') {
						const schemaReview = reviews.Reviews?.find((review) => review.OverallSatisfactionRating === Math.max(...reviews.Reviews.map((review) => review.OverallSatisfactionRating)));

						return (
							<>
								<ReviewsSlider
									id={panel.reviews.anchor_text}
									title={panel.reviews.heading}
									cta={panel.reviews.cta}
									reviews={reviews}
									key={index}
								/>

								<script
									type='application/ld+json'
									dangerouslySetInnerHTML={{
										__html: `{
										"@context": "https://schema.org",
										"@type": "Product",
										"name": "${pageData.seo.title}${pageData.seo.trail ? ` | ${pageData.seo.trail}` : ''}",
										"url": "${process.env.GATSBY_SITE_URL + pageData.url}",
										"brand": {
											"@type": "Brand",
											"name": "${pageData.seo.title}${pageData.seo.trail ? ` | ${pageData.seo.trail}` : ''}"
										},
										"description": "Discover information about our showroom in ${showroomData.city}, ${showroomData.state}. Browse our hours, contact details, top products, promotions and more.",
										"image": ["https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt5d51af0d3a33260d/63615b77ff7b405f6b3f58e9/pella-logo-black-spot.png"],
										"mpn": "",
										"sku": "",
										"aggregateRating": {
											"@type": "AggregateRating",
											"ratingValue": ${reviews.OverallSatisfactionRating},
											"reviewCount": ${reviews.TotalReviews},
											"bestRating": ${Math.max(...reviews.Reviews.map((review) => review.OverallSatisfactionRating))},
											"worstRating": ${Math.min(...reviews.Reviews.map((review) => review.OverallSatisfactionRating))}
										},
										"review":{
											"@type": "Review",
											"reviewBody": "${stripTags(schemaReview.WhyChosePella)}",
											"datePublished": "${schemaReview.DateSurveyed.split('T')[0]}",
											"author":{
												"@type": "Person",
												"name":"${schemaReview.CustomerFirstName} ${schemaReview.CustomerLastName}"
											},
											"reviewRating":{
												"@type": "Rating",
												"ratingValue": ${schemaReview.OverallSatisfactionRating}
											}
										}
									}`,
									}}
								/>
							</>
						);
					}

					if (panel.reviews != null && panel.reviews.showroom_ratings_id) {
						return (
							<div
								key={index}
								id={panel.reviews.anchor_text}
								className='block-reviews-elfsight py-6 md:py-8'
							>
								<Container>
									<ElfSightEmbed elfsightId={panel.reviews.showroom_ratings_id} />
								</Container>
							</div>
						);
					}

					if (panel.locations != null && isValidArray(pageData.branch_data) && showrooms?.filter((showroom) => showroom.store_number !== showroomData?.store_number && (panel.locations.by_state ? showroom.state === showroomData?.state : true))?.length > 0) {
						return (
							<div
								id={panel.locations.anchor}
								className='branchShowroom-locations py-6 md:py-8 xl:py-10'
								key={index}
							>
								<Container>
									{panel.locations.heading && (
										<h2
											className='mb-3 text-xl font-semibold xl:text-28px'
											dangerouslySetInnerHTML={{ __html: panel.locations.heading }}
										/>
									)}
									<NearbyShowrooms
										error={error}
										locations={showrooms.filter((showroom) => showroom.store_number !== showroomData?.store_number && (pageData.branch_data?.[0]?.by_state ? showroom.state === showroomData?.state : true))}
									/>
								</Container>
							</div>
						);
					}
				})}

			<VisualJourneyModal
				isOpen={isOpen === 'visual-journey'}
				redirect={configData?.cta?.[1]?.link?.href}
				onClose={onModalCloseClick}
			/>

			{showroomData && status !== 'loading' && (
				<LocalShowroom
					data={{
						...showroomData,
						description: pageData.seo.description,
						socials: socialArray,
						awards: awardArray,
						memberOf: memberOfArray,
						funder: funderArray,
						offer: data?.getPromotionsByBranchId?.[0] || {},
					}}
					url={pageData.url}
					areaServed={pageData.areas_served?.replace('Also serving the ', '')?.replace(' areas', '')?.replace(' and ', ', ')?.split(', ')}
					mapUrl={`https://www.google.com/maps/place/${mapAddress?.replace(/\s+/g, '+')}`}
				/>
			)}
		</Layout>
	);
};

BranchShowroom.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default BranchShowroom;

// eslint-disable-next-line react/prop-types -- fix? gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackBranchShowroom.seo} />;

export const query = graphql`
	query BranchShowroomQuery($uid: String!, $showroom_id: String!, $branch_id: [String], $store_numbers: [String]) {
		contentstackBranchShowroom(id: { eq: $uid }) {
			url
			showroom_id
			page_heading
			branch_id
			branch_data {
				...BranchData
			}
			hide_promo
			areas_served
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			also_serving
			directions
			showroom_images {
				layout
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
			}
			panels {
				cards {
					...CardSection
				}
				about {
					anchor
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					heading
					description
					social {
						...CTA
					}
				}
				dynamic {
					gray_background
					anchor_text
					heading
					description
					dynamic {
						...Dynamic
					}
				}
				reviews {
					anchor_text
					heading
					description
					cta {
						...CTA
					}
					showroom_ratings_id
				}
				locations {
					anchor
					heading
					branch_id
					by_state
				}
			}
			seo {
				...SEO
			}
		}

		showroom: showrooms(store_number: { eq: $showroom_id }) {
			name
			hour_mon
			hour_tue
			hour_wed
			hour_thu
			hour_fri
			hour_sat
			hour_sun
			hour_note
			line_1
			line_2
			city
			state
			postal_code
			country
			fax
			phone
			service_number
			service_number_ext
			toll_free_phone
			location {
				coordinates
			}
			branch_id
		}

		showroomsByBranch: allShowrooms(filter: { branch_id: { in: $branch_id } }) {
			nodes {
				...Showroom
			}
		}

		showroomsByStoreNumber: allShowrooms(filter: { store_number: { in: $store_numbers } }) {
			nodes {
				...Showroom
			}
		}
	}
`;
